import { useState, useEffect } from "react";
import { Plus, Trash2, Loader2, FileText, KeyRound, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { mediaSrc } from "@/components/HeroVideo";

const inputCls = "rounded-lg border border-neutral-700 bg-black px-3 py-2 text-sm text-neutral-100 outline-none focus:border-white";

export function ProposalBuilder({ req, onDone }) {
  const [items, setItems] = useState(req.proposal_items?.length ? req.proposal_items : [{ description: "", qty: 1, unit: "" }]);
  const [notes, setNotes] = useState(req.proposal_notes || "");
  const [pct, setPct] = useState(req.deposit_pct || 25);
  const [busy, setBusy] = useState(false);
  const total = items.reduce((a, i) => a + (Number(i.qty) || 0) * (Number(i.unit) || 0), 0);
  const upd = (i, k, v) => setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const send = async () => {
    const clean = items.filter((i) => i.description && Number(i.unit) > 0).map((i) => ({ description: i.description, qty: Number(i.qty) || 1, unit: Number(i.unit) }));
    if (!clean.length) return toast.error("Add at least one priced line item.");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/requests/${req.id}/proposal`, { items: clean, notes, deposit_pct: Number(pct), site: window.location.origin });
      toast.success(`Proposal sent — $${data.total.toLocaleString()} (deposit $${data.deposit.toLocaleString()})`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 border border-neutral-800 bg-black p-4" data-testid={`proposal-builder-${req.id}`}>
      <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-white"><FileText className="h-4 w-4" /> Proposal builder</div>
      <div className="mt-3 space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2">
            <input placeholder="Line item (e.g. Stage, sound & lights)" value={it.description} onChange={(e) => upd(i, "description", e.target.value)} data-testid={`proposal-desc-${i}`} className={`${inputCls} col-span-6`} />
            <input type="number" placeholder="Qty" value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} data-testid={`proposal-qty-${i}`} className={`${inputCls} col-span-2`} />
            <input type="number" placeholder="Unit $" value={it.unit} onChange={(e) => upd(i, "unit", e.target.value)} data-testid={`proposal-unit-${i}`} className={`${inputCls} col-span-3`} />
            <button onClick={() => setItems((xs) => xs.filter((_, idx) => idx !== i))} className="col-span-1 text-neutral-500 hover:text-white"><Trash2 className="mx-auto h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <button onClick={() => setItems((xs) => [...xs, { description: "", qty: 1, unit: "" }])} data-testid="proposal-add-item" className="mt-2 flex items-center gap-1 text-xs uppercase tracking-widest text-neutral-400 hover:text-white"><Plus className="h-3 w-3" /> Add line</button>
      <textarea rows={3} placeholder="Notes / assumptions / what's excluded" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="proposal-notes" className={`${inputCls} mt-3 w-full`} />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-neutral-400">Deposit % <input type="number" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="proposal-pct" className={`${inputCls} w-20`} /></label>
        <div className="text-right"><div className="text-xs uppercase tracking-widest text-neutral-500">Total</div><div className="font-display text-2xl text-white" data-testid="proposal-builder-total">${total.toLocaleString()}</div></div>
        <button onClick={send} disabled={busy} data-testid="proposal-send-btn" className="btn-gold flex items-center gap-2 px-5 py-2.5 text-[11px] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate PDF & email client"}</button>
      </div>
      {req.proposal_file && <a href={mediaSrc(`/api/media/files/${req.proposal_file}`)} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-neutral-400 underline" data-testid="proposal-existing-pdf">View last sent proposal PDF</a>}
    </div>
  );
}

export function TeamPanel() {
  const [team, setTeam] = useState([]);
  const [f, setF] = useState({ email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/team").then((r) => setTeam(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invite", { ...f, origin_url: window.location.origin });
      toast.success(data.email_sent ? `Invitation emailed to ${data.email}.` : `Invite created for ${data.email} (email not configured — copy the link).`);
      setF({ email: "", name: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 max-w-lg border border-neutral-800 bg-black p-6" data-testid="team-panel">
      <div className="flex items-center gap-2 font-display text-lg text-white"><UserPlus className="h-5 w-5" /> Team access</div>
      <p className="mt-1 text-xs text-neutral-500">Send a secure invitation. The recipient sets their own password from a single-use link (72 h). No passwords are emailed.</p>
      <form onSubmit={invite} className="mt-5 grid gap-3">
        <input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name" className={inputCls} />
        <input type="email" required placeholder="Email to invite" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email-input" className={inputCls} />
        <button type="submit" disabled={busy} data-testid="invite-send-btn" className="btn-gold px-6 py-3 text-[11px] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send invitation"}</button>
      </form>
      <ul className="mt-5 space-y-2">
        {team.map((u) => (
          <li key={u.id} className="flex items-center justify-between border border-neutral-800 px-3 py-2 text-xs" data-testid={`team-member-${u.email}`}>
            <span className="text-stone-100">{u.name} <span className="text-neutral-500">· {u.email}</span></span>
            <span className={`rounded-full px-2 py-0.5 font-mono-label text-[10px] uppercase ${u.status === "active" ? "bg-green-500/20 text-green-400" : "bg-stone-700 text-neutral-300"}`}>{u.status}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AccountPanel({ user }) {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "", email: user?.email || "", name: user?.name || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    if (f.new_password && f.new_password !== f.confirm) return toast.error("New passwords don't match.");
    setBusy(true);
    try {
      await api.put("/admin/account", { current_password: f.current_password, new_password: f.new_password, email: f.email, name: f.name });
      toast.success("Account updated. Use your new details next time you log in.");
      setF((x) => ({ ...x, current_password: "", new_password: "", confirm: "" }));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="mt-6 max-w-lg border border-neutral-800 bg-black p-6" data-testid="account-panel">
      <div className="flex items-center gap-2 font-display text-lg text-white"><KeyRound className="h-5 w-5" /> Account & password</div>
      <p className="mt-1 text-xs text-neutral-500">Change the login email and password you received in the handoff. Current password is required.</p>
      <div className="mt-5 grid gap-3">
        <input placeholder="Display name" value={f.name} onChange={set("name")} data-testid="account-name" className={inputCls} />
        <input type="email" placeholder="Login email" value={f.email} onChange={set("email")} data-testid="account-email" className={inputCls} />
        <input type="password" required placeholder="Current password" value={f.current_password} onChange={set("current_password")} data-testid="account-current" className={inputCls} />
        <input type="password" placeholder="New password (min 8)" value={f.new_password} onChange={set("new_password")} data-testid="account-new" className={inputCls} />
        <input type="password" placeholder="Confirm new password" value={f.confirm} onChange={set("confirm")} data-testid="account-confirm" className={inputCls} />
      </div>
      <button type="submit" disabled={busy} data-testid="account-save-btn" className="btn-gold mt-5 px-6 py-3 text-[11px] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</button>
    </form>
  );
}
